import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Calendar, CreditCard, CheckCircle, AlertCircle, Filter, Search } from 'lucide-react';
import AuroraFloatingCard from '../ui/AuroraFloatingCard';
interface Invoice {
  id: string;
  invoiceNumber: string;
  date: string;
  amount: number;
  status: 'paid' | 'pending' | 'failed';
  description: string;
  period: string;
  pdfUrl?: string;
}
export function FaturaGecmisi() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'paid' | 'pending' | 'failed'>('all');
  // Mock invoice data with realistic corporate pricing
  const invoices: Invoice[] = [{
    id: '1',
    invoiceNumber: 'INV-2024-001',
    date: '2024-09-01',
    amount: 2250000,
    status: 'paid',
    description: 'LGS Kurumsal Paket - Yıllık Abonelik (350 Öğrenci)',
    period: '2024-09-01 / 2025-09-01',
    pdfUrl: '#'
  }, {
    id: '2',
    invoiceNumber: 'INV-2023-012',
    date: '2023-09-01',
    amount: 1950000,
    status: 'paid',
    description: 'LGS Kurumsal Paket - Yıllık Abonelik (300 Öğrenci)',
    period: '2023-09-01 / 2024-09-01',
    pdfUrl: '#'
  }, {
    id: '3',
    invoiceNumber: 'INV-2023-006',
    date: '2023-03-15',
    amount: 325000,
    status: 'paid',
    description: 'Ek Öğrenci Lisansı (50 Öğrenci)',
    period: '2023-03-15 / 2024-03-15',
    pdfUrl: '#'
  }, {
    id: '4',
    invoiceNumber: 'INV-2022-024',
    date: '2022-12-01',
    amount: 1625000,
    status: 'paid',
    description: 'LGS Kurumsal Paket - Yıllık Abonelik (250 Öğrenci)',
    period: '2022-12-01 / 2023-12-01',
    pdfUrl: '#'
  }, {
    id: '5',
    invoiceNumber: 'INV-2022-018',
    date: '2022-08-10',
    amount: 450000,
    status: 'paid',
    description: 'Premium Destek Paketi - Yıllık',
    period: '2022-08-10 / 2023-08-10',
    pdfUrl: '#'
  }];
  const getStatusInfo = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return {
          text: 'Ödendi',
          color: 'text-green-400',
          bgColor: 'bg-green-500/10',
          icon: CheckCircle
        };
      case 'pending':
        return {
          text: 'Beklemede',
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-500/10',
          icon: AlertCircle
        };
      case 'failed':
        return {
          text: 'Başarısız',
          color: 'text-red-400',
          bgColor: 'bg-red-500/10',
          icon: AlertCircle
        };
    }
  };
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) || invoice.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || invoice.status === filterStatus;
    return matchesSearch && matchesFilter;
  });
  const totalPaid = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.amount, 0);
  return <div className="space-y-6 p-6 min-h-screen bg-[#0D1117] text-white" data-id="element-432">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-433">
        <div className="flex items-center gap-4" data-id="element-434">
          <button onClick={() => navigate('/yonetici/profil')} className="p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors text-white" data-id="element-435">
            <ArrowLeft className="w-5 h-5" data-id="element-436" />
          </button>
          <div data-id="element-437">
            <h1 className="text-3xl font-bold text-white" data-id="element-438">Fatura Geçmişi</h1>
            <p className="text-gray-400 mt-1" data-id="element-439">
              Tüm faturalarınızı görüntüleyin ve indirin
            </p>
          </div>
        </div>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-440">
        <AuroraFloatingCard data-id="element-441">
          <div className="p-5" data-id="element-442">
            <div className="flex items-center gap-3 mb-2" data-id="element-443">
              <FileText className="w-5 h-5 text-blue-400" data-id="element-444" />
              <span className="text-sm text-gray-400" data-id="element-445">Toplam Fatura</span>
            </div>
            <p className="text-2xl font-bold text-white" data-id="element-446">{invoices.length}</p>
          </div>
        </AuroraFloatingCard>
        <AuroraFloatingCard data-id="element-447">
          <div className="p-5" data-id="element-448">
            <div className="flex items-center gap-3 mb-2" data-id="element-449">
              <CheckCircle className="w-5 h-5 text-green-400" data-id="element-450" />
              <span className="text-sm text-gray-400" data-id="element-451">Ödenen</span>
            </div>
            <p className="text-2xl font-bold text-white" data-id="element-452">
              {invoices.filter(inv => inv.status === 'paid').length}
            </p>
          </div>
        </AuroraFloatingCard>
        <AuroraFloatingCard data-id="element-453">
          <div className="p-5" data-id="element-454">
            <div className="flex items-center gap-3 mb-2" data-id="element-455">
              <CreditCard className="w-5 h-5 text-purple-400" data-id="element-456" />
              <span className="text-sm text-gray-400" data-id="element-457">Toplam Tutar</span>
            </div>
            <p className="text-2xl font-bold text-white" data-id="element-458">
              {totalPaid.toLocaleString('tr-TR')} ₺
            </p>
          </div>
        </AuroraFloatingCard>
      </div>
      {/* Filters */}
      <AuroraFloatingCard data-id="element-459">
        <div className="p-4" data-id="element-460">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-461">
            {/* Search */}
            <div className="flex-1 relative" data-id="element-462">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-463" />
              <input type="text" placeholder="Fatura numarası veya açıklama ara..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-gray-800/50 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50" data-id="element-464" />
            </div>
            {/* Status Filter */}
            <div className="flex items-center gap-2" data-id="element-465">
              <Filter className="w-5 h-5 text-gray-400" data-id="element-466" />
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as any)} className="px-4 py-2 bg-gray-800/50 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50" data-id="element-467">
                <option value="all" data-id="element-468">Tüm Faturalar</option>
                <option value="paid" data-id="element-469">Ödenenler</option>
                <option value="pending" data-id="element-470">Bekleyenler</option>
                <option value="failed" data-id="element-471">Başarısızlar</option>
              </select>
            </div>
          </div>
        </div>
      </AuroraFloatingCard>
      {/* Invoices List */}
      <div className="space-y-3" data-id="element-472">
        {filteredInvoices.length > 0 ? filteredInvoices.map(invoice => {
        const statusInfo = getStatusInfo(invoice.status);
        const StatusIcon = statusInfo.icon;
        return <AuroraFloatingCard key={invoice.id} data-id="element-473">
                <div className="p-5" data-id="element-474">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-475">
                    {/* Left Section */}
                    <div className="flex-1" data-id="element-476">
                      <div className="flex items-start gap-4" data-id="element-477">
                        <div className="p-3 bg-purple-500/10 rounded-lg" data-id="element-478">
                          <FileText className="w-6 h-6 text-purple-400" data-id="element-479" />
                        </div>
                        <div className="flex-1" data-id="element-480">
                          <div className="flex items-center gap-3 mb-1" data-id="element-481">
                            <h3 className="text-lg font-semibold text-white" data-id="element-482">
                              {invoice.invoiceNumber}
                            </h3>
                            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${statusInfo.bgColor}`} data-id="element-483">
                              <StatusIcon className={`w-4 h-4 ${statusInfo.color}`} data-id="element-484" />
                              <span className={`text-xs font-medium ${statusInfo.color}`} data-id="element-485">
                                {statusInfo.text}
                              </span>
                            </div>
                          </div>
                          <p className="text-gray-300 mb-2" data-id="element-486">
                            {invoice.description}
                          </p>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400" data-id="element-487">
                            <div className="flex items-center gap-1.5" data-id="element-488">
                              <Calendar className="w-4 h-4" data-id="element-489" />
                              <span data-id="element-490">
                                {new Date(invoice.date).toLocaleDateString('tr-TR')}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-491">
                              <Calendar className="w-4 h-4" data-id="element-492" />
                              <span data-id="element-493">Dönem: {invoice.period}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Right Section */}
                    <div className="flex items-center gap-4" data-id="element-494">
                      <div className="text-right" data-id="element-495">
                        <p className="text-2xl font-bold text-white" data-id="element-496">
                          {invoice.amount.toLocaleString('tr-TR')} ₺
                        </p>
                      </div>
                      {invoice.status === 'paid' && <button onClick={() => alert('PDF indirilecek...')} className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors text-white" data-id="element-497">
                          <Download className="w-4 h-4" data-id="element-498" />
                          <span className="hidden sm:inline" data-id="element-499">İndir</span>
                        </button>}
                    </div>
                  </div>
                </div>
              </AuroraFloatingCard>;
      }) : <AuroraFloatingCard data-id="element-500">
            <div className="p-12 text-center" data-id="element-501">
              <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" data-id="element-502" />
              <p className="text-gray-400 text-lg" data-id="element-503">Fatura bulunamadı</p>
              <p className="text-gray-500 text-sm mt-2" data-id="element-504">
                Arama kriterlerinizi değiştirmeyi deneyin
              </p>
            </div>
          </AuroraFloatingCard>}
      </div>
    </div>;
}